import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useBackend } from 'context/BackendContext'
import { Product } from 'types/Product'
import { useSafeCookieContext } from './useSafeCookieContext'

const useProductInfo = (): {
  productInfo: Product[] | null
  loading: boolean
  error: Error | undefined
} => {
  const { productInfo: serverProductInfo, setProductInfo } = useBackend()
  const router = useRouter()
  const { cookieCountry } = useSafeCookieContext()

  const [clientProductInfo, setClientProductInfo] = useState<Product[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>(undefined)

  const localeInUrl = router.locale?.toLowerCase() // 'en-gb', 'en-us', etc
  const countryFromLocale = router.locale?.split('-')[1].toLowerCase() // 'gb', 'us', etc
  const countryToUse = cookieCountry?.toLowerCase() || countryFromLocale // Prefer cookie if available

  useEffect(() => {
    if (!localeInUrl || !countryToUse) return

    const fetchData = async () => {
      setLoading(true)
      try {
        const res = await fetch(`/data-product-info/${localeInUrl}/${countryToUse}`)
        if (!res.ok) throw new Error(`Failed to fetch: ${res.statusText}`)
        const json = await res.json()
        // Only update clientProductInfo if data actually changed
        if (json?.productInfo) {
          setClientProductInfo(json.productInfo)
          setProductInfo(json.productInfo) // update context
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown fetch error'))
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [localeInUrl, cookieCountry, countryToUse, countryFromLocale, setProductInfo])

  const products = clientProductInfo || serverProductInfo

  return {
    productInfo: products,
    loading,
    error,
  }
}

export default useProductInfo
